import type { MyRestrictions } from '@yawi/types';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useDI } from './DIContext';
import { useAuth } from './AuthContext';

interface RestrictionsContextType {
  data: MyRestrictions | null;
  /** true mientras aún no sabemos el estado (primera carga). */
  loading: boolean;
  /** Listado Negro: solo el módulo de pagos está disponible. */
  isBlacklisted: boolean;
  refresh: () => Promise<void>;
}

const RestrictionsContext = createContext<RestrictionsContextType | undefined>(undefined);

const BLOCKED_STATE: MyRestrictions = {
  hasRestrictions: true,
  restrictedActions: ['*'],
  debtAmount: 0,
  isCobranza: true,
  isBlacklisted: true,
};

export const RestrictionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { guardRepo, setOnBlacklistedHandler } = useDI();
  const { currentUser } = useAuth();
  const [data, setData] = useState<MyRestrictions | null>(null);
  const [loading, setLoading] = useState(true);

  const hasUser = !!currentUser;

  const refresh = useCallback(async () => {
    if (!hasUser) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      setData(await guardRepo.getMyRestrictions());
    } catch {
      // Un fallo de red NO desbloquea: se conserva el último estado conocido.
      // El endurecimiento real es el 403 del backend.
    } finally {
      setLoading(false);
    }
  }, [guardRepo, hasUser]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Un admin puede aplicar el bloqueo mientras la pestaña está en segundo
  // plano: al volver el foco se revalida sin esperar a un reload.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  // Primer 403 UNIT_BLACKLISTED de cualquier repositorio → bloqueo inmediato.
  useEffect(() => {
    setOnBlacklistedHandler(() => {
      setData((prev) => ({ ...BLOCKED_STATE, debtAmount: prev?.debtAmount ?? 0 }));
      setLoading(false);
    });
  }, [setOnBlacklistedHandler]);

  return (
    <RestrictionsContext.Provider
      value={{ data, loading, isBlacklisted: data?.isBlacklisted === true, refresh }}
    >
      {children}
    </RestrictionsContext.Provider>
  );
};

export const useRestrictions = (): RestrictionsContextType => {
  const context = useContext(RestrictionsContext);
  if (!context) {
    throw new Error('useRestrictions debe usarse dentro de RestrictionsProvider');
  }
  return context;
};
